export class WidgetCommunicationService {
  private static allowedOrigins: string[] = [];
  private static messageHandlers: Map<string, (data: any) => void> = new Map();

  /**
   * Initialize the widget communication service
   */
  static init(allowedOrigins: string[] = []) {
    this.allowedOrigins = allowedOrigins;
    
    // Listen for messages
    window.addEventListener('message', this.handleMessage.bind(this));
  }

  /**
   * Add allowed origin for embedding
   */
  static addAllowedOrigin(origin: string) {
    if (!this.allowedOrigins.includes(origin)) {
      this.allowedOrigins.push(origin);
    }
  }

  /**
   * Register a message handler
   */
  static on(type: string, handler: (data: any) => void) {
    this.messageHandlers.set(type, handler);
  }

  /**
   * Send message to parent window
   */
  static sendToParent(type: string, data?: any) {
    if (window.parent !== window) {
      window.parent.postMessage({ type, ...data }, '*');
    }
  }

  /**
   * Send message to iframe
   */
  static sendToIframe(iframe: HTMLIFrameElement, type: string, data?: any) {
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({ type, ...data }, '*');
    }
  }

  /**
   * Handle incoming messages
   */
  private static handleMessage(event: MessageEvent) {
    // In production, verify origin
    if (this.allowedOrigins.length > 0 && !this.allowedOrigins.includes(event.origin)) {
      console.warn('Message from unauthorized origin:', event.origin);
      return;
    }

    if (event.data && event.data.type) {
      const handler = this.messageHandlers.get(event.data.type);
      if (handler) {
        handler(event.data);
      }
    }
  }

  /**
   * Check if running in iframe
   */
  static isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  /**
   * Get parent origin (if in iframe)
   */
  static getParentOrigin(): string | null {
    if (this.isInIframe() && document.referrer) {
      try {
        const url = new URL(document.referrer);
        return url.origin;
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  /**
   * Standard widget messages
   */
  static messages = {
    ready: () => this.sendToParent('helpgenie:ready'),
    close: () => this.sendToParent('helpgenie:close'),
    resize: (height: number) => this.sendToParent('helpgenie:resize', { height }),
    conversationStarted: (agentId: string, conversationId?: string) =>
      this.sendToParent('helpgenie:conversation:started', { agentId, conversationId }),
    conversationEnded: (agentId: string, conversationId: string) =>
      this.sendToParent('helpgenie:conversation:ended', { agentId, conversationId }),
    leadCaptured: (lead: {
      agentId: string;
      conversationId?: string;
      source: string;
      googleSendTo?: string;
      metaLead?: boolean;
    }) => this.sendToParent('helpgenie:lead:captured', lead),
    error: (error: string) => this.sendToParent('helpgenie:error', { error })
  };
}

// Export singleton instance
export default WidgetCommunicationService;